import type { ApiEndpointBuilder } from "../apiEndpointHelpers";

import type {
  AdminV2AutomationTenantBindingInput,
  AdminV2AutomationTenantBindingsMutationResponse,
  AdminV2AutomationTenantBindingsResponse,
} from "@/types/adminV2AutomationBindings";

export interface BindingTarget {
  automationId: string;
  organisationId: string;
}
const bindingUrl = ({ automationId, organisationId }: BindingTarget) =>
  `/admin/v2/automations/${encodeURIComponent(automationId)}/tenant-bindings/${encodeURIComponent(organisationId)}`;
const bindingTags = (target: BindingTarget) => [
  {
    type: "AdminV2Automations" as const,
    id: `BINDINGS_${target.automationId}_${target.organisationId}`,
  },
];

/** Tenant bindings have their own cache identity; never reuse another tenant's result. */
export const buildAdminV2AutomationBindingEndpoints = (
  builder: ApiEndpointBuilder,
) => ({
  getAdminV2AutomationTenantBindings: builder.query<
    AdminV2AutomationTenantBindingsResponse,
    BindingTarget & { publishedVersionId: string }
  >({
    query: (target) => ({
      url: bindingUrl(target),
      params: { publishedVersionId: target.publishedVersionId },
    }),
    providesTags: (_result, _error, target) => bindingTags(target),
  }),
  updateAdminV2AutomationTenantBindings: builder.mutation<
    AdminV2AutomationTenantBindingsMutationResponse,
    BindingTarget & {
      pinnedAutomationVersionId: string;
      state: "disabled" | "ready";
      bindings: AdminV2AutomationTenantBindingInput[];
    }
  >({
    query: ({ automationId, organisationId, ...body }) => ({
      url: bindingUrl({ automationId, organisationId }),
      method: "PUT",
      data: body,
    }),
    invalidatesTags: (_result, _error, target) => bindingTags(target),
  }),
  transitionAdminV2AutomationTenantBindings: builder.mutation<
    AdminV2AutomationTenantBindingsMutationResponse,
    BindingTarget & {
      transition: "activate" | "pause";
      pinnedAutomationVersionId: string;
    }
  >({
    query: ({ transition, pinnedAutomationVersionId, ...target }) => ({
      url: `${bindingUrl(target)}/${transition}`,
      method: "POST",
      data: { pinnedAutomationVersionId },
    }),
    invalidatesTags: (_result, _error, target) => bindingTags(target),
  }),
});
